import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import { AccessKind, Role, TaskKind, User } from "../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import { useAppSelector } from "../../app/hooks";
import UserSpan from "../common/users/UserSpan";
import Morselbar from "../common/Morselbar";

export default function CatalogAccessDialog(props: {
  onboarded: boolean;
  open: boolean;
  setOpen: (val: boolean) => void;
  callback: () => void;
  catalogName: string;
  catalogId: string;
  user: User;
}) {
  const { onboarded, open, setOpen, callback, catalogName, catalogId, user } =
    props;

  const boardingAction = onboarded ? "offboarded" : "onboarded";
  const boardingPreposition = onboarded ? "from" : "in";
  const boardingColor = onboarded ? "error" : "success.main";
  const confirmColor = onboarded ? "error" : "success";

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [createNote, setCreateNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createBoardingTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = createNote || undefined;

    const body: TaskKind = {
      kind: "CatalogAccess",
      inner: {
        kind: onboarded ? AccessKind.Off : AccessKind.On,
        catalog_name: catalogName,
        catalog_id: catalogId,
        target: user.employee.id,
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => {
        callback();
        setOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    createNote,
    onboarded,
    catalogName,
    catalogId,
    user.employee.id,
    client,
    override,
    callback,
    setOpen,
  ]);

  return (
    <>
      <Dialog open={open}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              <UserSpan user={user} variant="body1" /> will be{" "}
              <Typography component="span" color={boardingColor}>
                {boardingAction}
              </Typography>{" "}
              {boardingPreposition}{" "}
              <Typography component="span" color="secondary">
                {catalogName}
              </Typography>
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <br />
          <TextField
            onChange={(e) => setCreateNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && !onboarded && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={override}
                      onChange={(e) => setOverride(e.target.checked)}
                    />
                  }
                  label="Skip approvals"
                />
              )}
            </Box>

            <Button
              onClick={() => createBoardingTask()}
              disabled={loading}
              color={confirmColor}
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}
